import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { useTranslation } from 'react-i18next';
import { useDialogContext } from 'hooks/context';
import { CContainedButton, COutlinedButton } from 'components/atoms';
import { useEffect } from 'react';

const GlobalDialog = () => {
  const { t } = useTranslation('common');
  const { state, closeDialog, closeAfterTimeoutDialog } = useDialogContext();
  const {
    open,
    onApprove,
    onApproveTitle,
    onClose,
    onCloseTitle,
    title,
    message,
    actions,
    children,
    enableBackdropClick,
    closeAfterApprove = true,
    closeAfterTimeout,
    onCloseAfterTimeout,
    isCloseButtonDisabled,
    isApproveButtonDisabled,
    type,
  } = state;

  const types = {
    warning: {
      src: new URL('assets/svg/warning-dialog.svg', import.meta.url).href,
      title: t('warning'),
    },
    error: {
      src: new URL('assets/svg/error.svg', import.meta.url).href,
      title: t('error'),
    },
    success: {
      src: new URL('assets/svg/success.svg', import.meta.url).href,
      title: t('success'),
    },
  };

  const iconType = type && types[type];

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
    closeDialog();
  };

  const handleApprove = () => {
    if (onApprove) {
      onApprove();
    }
    closeAfterApprove && closeDialog();
  };

  useEffect(() => {
    closeAfterTimeout && closeAfterTimeoutDialog(closeAfterTimeout, onCloseAfterTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closeAfterTimeout]);

  return (
    <Dialog
      open={open}
      onClose={enableBackdropClick ? handleClose : undefined}
      aria-labelledby={`dialog-${title}`}
      aria-describedby={`dialog-${message}`}
      classes={{
        paper: 'flex flex-col gap-4 rounded-xl px-4 py-7 text-center md:px-8 md:py-14',
      }}
    >
      {title && (
        <DialogTitle className="p-0 text-label20 text-neutral-700" id="dialog-title">
          {title}
        </DialogTitle>
      )}

      {iconType && <img className="w-20 self-center" src={iconType.src} alt={iconType.title} />}

      {message && (
        <DialogContent className="p-0">
          <DialogContentText className="text-label16 text-neutral-600" id="dialog-message">
            {message}
          </DialogContentText>
        </DialogContent>
      )}

      {children}

      {(onApprove || onClose || actions) && (
        <DialogActions className="justify-center gap-2 p-0 md:gap-5">
          {onClose && (
            <COutlinedButton className="flex-1" disabled={isCloseButtonDisabled} onClick={handleClose}>
              {onCloseTitle ?? t('close')}
            </COutlinedButton>
          )}

          {onApprove && (
            <CContainedButton
              disabled={isApproveButtonDisabled}
              className="!ml-0 flex-1"
              onClick={handleApprove}
            >
              {onApproveTitle ?? t('confirm')}
            </CContainedButton>
          )}

          {actions}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default GlobalDialog;

